import { ReactNode, useContext, useRef, useState } from 'react'
import {
  ControllerRenderProps,
  FieldValues,
  useFormContext,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormEntityContext } from '../../config/context/forms'
import FieldWrapper from './FieldWrapper'
import { FormField } from '@/components/ui/form'
import {
  ColorFormat,
  ColorPicker,
  ColorPickerArea,
  ColorPickerContent,
  ColorPickerHueSlider,
  ColorPickerInput,
  ColorPickerSwatch,
  ColorPickerTrigger,
} from '@/components/ui/color-picker'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Undo2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Props {
  name: string
  layout?: 'box' | 'inline' | 'stacked' | 'inline-shrinkable'
  customLabel?: ReactNode
  customDescription?: string
  customPlaceholder?: string
  className?: string
  allowImageUrl?: boolean
  originalValue?: string | null
  format?: ColorFormat
  onChange?: (value: string) => void
}

const ColorField = ({
  name,
  customDescription,
  customLabel,
  format = 'hex',
  layout = 'box',
  allowImageUrl = false,
  originalValue = null,
  onChange,
}: Props) => {
  const { t } = useTranslation()
  const form = useFormContext()
  const { entityType, i18nPrefix } = useContext(FormEntityContext)

  const resetToDefaultAt = form.watch('resetToDefaultAt')

  return (
    <div className={`${layout === 'inline-shrinkable' ? '' : 'w-full'}`}>
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => {
          return (
            <FieldWrapper
              className='space-y-1'
              layout={layout}
              label={
                customLabel ?? t(`${i18nPrefix}.${entityType}.${name}.label`)
              }
              description={
                customDescription ??
                t(`${i18nPrefix}.${entityType}.${name}.description`)
              }
            >
              <ColorFieldContent
                key={resetToDefaultAt ?? ''}
                format={format}
                originalValue={originalValue}
                field={field}
                allowImageUrl={allowImageUrl}
                onChange={onChange}
              />
            </FieldWrapper>
          )
        }}
      />
    </div>
  )
}

const ColorFieldContent = ({
  originalValue,
  field,
  format = 'hex',
  allowImageUrl,
  onChange,
}: {
  originalValue: string | null
  format: ColorFormat
  field: ControllerRenderProps<FieldValues, string>
  allowImageUrl: boolean
  onChange?: (value: string) => void
}) => {
  const [value, setValue] = useState(field.value || originalValue)
  const [mode, setMode] = useState<'color' | 'image'>(
    allowImageUrl && field.value?.startsWith('http') ? 'image' : 'color',
  )
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>()

  const debouncedOnChange = (val: string) => {
    clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => onChange?.(val), 50)
  }

  const handleModeChange = (value: 'color' | 'image') => {
    setMode(value)
    field.onChange(value === 'image' ? '' : field.value)
  }

  return (
    <ColorPicker
      value={value}
      format={format}
      onValueChange={(value) => {
        if (mode === 'color') {
          setValue(value)
          debouncedOnChange(value)
        }
      }}
      onOpenChange={(open) => {
        if (!open) {
          field.onChange(value)
        }
      }}
    >
      <div className='flex items-center gap-0.5'>
        <ColorPickerTrigger asChild>
          <Button variant='ghost' className='p-0 border h-auto '>
            {mode === 'color' ? (
              <ColorPickerSwatch className='hover:border-muted-foreground transition-colors w-18.5 h-6' />
            ) : (
              <div
                className='w-18.5 h-6 bg-muted-foreground rounded-sm bg-cover bg-center border hover:border-muted-foreground transition-colors'
                style={{ backgroundImage: `url(${field.value})` }}
              />
            )}
          </Button>
        </ColorPickerTrigger>

        <Undo2
          size={18}
          onClick={() => {
            setMode(originalValue?.startsWith('http') ? 'image' : 'color')
            setValue(originalValue)
            field.onChange(originalValue)
            if (originalValue) onChange?.(originalValue)
          }}
          className={cn(
            'cursor-pointer hover:text-accent-foreground',
            field.value && originalValue !== field.value
              ? 'visible'
              : 'invisible pointer-events-none',
          )}
        />
      </div>
      <ColorPickerContent>
        {allowImageUrl && (
          <Tabs
            value={mode}
            onValueChange={(value) =>
              handleModeChange(value as 'color' | 'image')
            }
          >
            <TabsList className='w-full'>
              <TabsTrigger value='color' className='flex-1'>
                Color
              </TabsTrigger>
              <TabsTrigger value='image' className='flex-1'>
                Image URL
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        {mode === 'color' ? (
          <>
            <ColorPickerArea />
            <ColorPickerHueSlider />
            <ColorPickerInput withoutAlpha />
          </>
        ) : (
          <>
            <div
              className='rounded-sm border h-40 w-full'
              style={{ backgroundImage: `url(${field.value})` }}
            />
            <Input
              type='url'
              value={field.value}
              placeholder='Image URL'
              onChange={(e) => {
                if (mode === 'image') {
                  field.onChange(e.target.value)
                }
              }}
            />
          </>
        )}
      </ColorPickerContent>
    </ColorPicker>
  )
}

export default ColorField
